/* eslint-disable react-hooks/exhaustive-deps */
import { Grid, makeStyles, TextField } from '@material-ui/core';
import { AutoCompleteWithValue } from 'commons/AutoComplete/AutoCompleteWithValue';
import { DialogType } from 'commons/Dialog/CustomDialog';
import { localize } from 'commons/LocalizedText/LocalizedText';
import {
  CustomTableWithExternalPagination,
  INITIAL_PAGE,
  INITIAL_ROWS_PER_PAGE
} from 'commons/Table/CustomTableWithExternalPagination';
import ExcelExportButton from 'components/ExcelExportButton/ExcelExportButton';
import { PageContainer } from 'components/PageContainer/PageContainer';
import { SearchBar, SELECTED_PROGRAM_QUERY_KEY, SELECTED_YEARS_QUERY_KEY } from 'components/SearchBar/SearchBar';
import { useDialog } from 'hook/DialogProvider';
import useQueryState from 'hook/UseQueryState';
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { ProductService } from 'services/ProductService';
import { ProductTestService } from 'services/ProductTestService';
import { ErrorName } from 'utils/constants/error.name';
import { ParamTypes } from 'utils/constants/param.type';
import { GENERATE_ROUTE, ROUTES } from 'utils/constants/routes';
import { COLUMN_CHART_AXIS_LABEL, getHasBoundariesByStat, getStats } from 'utils/constants/stats';
import { convertJsonToXlsx } from 'utils/excelConverter';
import { QueryStringToJSONObject } from 'utils/helpers/QueryStringHelper';
import { mapProductRecords } from 'utils/mapper/testGridExportMapper';
import { addNewRows, testRows } from 'utils/rows/TestRows';
import { setQueryStringValue } from "utils/processing/url";
import { PART_LAB } from "utils/constants/partLab";
import { CustomButton } from 'commons/Button/CustomButton';

const useStyles = makeStyles((theme) => ({
  downloadButton: {
    color: theme.palette.primary.main
  },
  exclamation: {
    marginLeft: theme.spacing(1)
  },
  warning: {
    color: theme.palette.warning.main
  },
  error: {
    color: theme.palette.error.main
  },
  tableContainer: {
    maxHeight: '40vw',
    overflowX: 'auto',
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      height: '12px',
      width: '12px'
    },
    '&::-webkit-scrollbar-track': {
      marginTop: '90px',
      backgroundColor: theme.palette.primary.light
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.main
    }
  },
  tableFooter: {
    marginTop: '20px'
  },
  resultTitle: {
    margin: '4px 0px',
    padding: '5px 10px',
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    fontWeight: '600'
  }
}));

const SEARCH_STORAGE_KEY = "TEST_LIST_PAGE_SEARCH_FORM";

const STAT_QUERY_KEY = 'selectedStat';
const STAT_BOUNDARY_QUERY_KEY = 'selectedStatBoundary';
const COMPANY_NAME_QUERY_KEY = 'companyName';
const PART_LAB_QUERY_KEY = 'partLab';
const PRODUCT_NUMBER_QUERY_KEY = 'productNumber';
const TEST_NUMBER_QUERY_KEY = 'testNumber';
const PRODUCT_NAME_QUERY_KEY = 'productName';
const PRODUCT_TEST_TYPES_QUERY_KEY = 'productTestTypes';
const CUSTOMER_STATUSES_QUERY_KEY = 'customerStatuses';

export const TestListPage = () => {
  const { laboratory: laboratoryId } = useParams();
  const classes = useStyles();
  const history = useHistory();
  const { state, search } = useLocation();
  const showDialog = useDialog();
  const previousController = useRef();
  const listInnerRef = useRef();
  const [isLoadingProductTestFetch, setIsLoadingProductTestFetch] = useState(false);
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [numberOfRow, setNumberOfRow] = useState(0);

  const [selectedStat, setSelectedStat] = useQueryState(
    STAT_QUERY_KEY,
    state?.[STAT_QUERY_KEY] || null,
    ParamTypes.STRING
  );
  const [selectedStatBoundary, setSelectedStatBoundary] = useQueryState(
    STAT_BOUNDARY_QUERY_KEY,
    state?.[STAT_BOUNDARY_QUERY_KEY] || null,
    ParamTypes.STRING
  );
  const [companyName, setCompanyName] = useQueryState(COMPANY_NAME_QUERY_KEY, null, ParamTypes.STRING);
  const [partLab, setPartLab] = useQueryState(PART_LAB_QUERY_KEY, null, ParamTypes.STRING);
  const [productNumber, setProductNumber] = useQueryState(PRODUCT_NUMBER_QUERY_KEY, null, ParamTypes.STRING);
  const [testNumber, setTestNumber] = useQueryState(TEST_NUMBER_QUERY_KEY, null, ParamTypes.STRING);
  const [productName, setProductName] = useQueryState(
    PRODUCT_NAME_QUERY_KEY,
    state?.[PRODUCT_NAME_QUERY_KEY] || null,
    ParamTypes.STRING
  );
  let queryObject = QueryStringToJSONObject(window.location.search.substring(1));
  const [productTestTypes, setProductTestTypes] = useQueryState(
    PRODUCT_TEST_TYPES_QUERY_KEY,
    queryObject?.[PRODUCT_TEST_TYPES_QUERY_KEY] || [],
    ParamTypes.ARRAY
  );
  const [customerStatuses, setCustomerStatuses] = useQueryState(
    CUSTOMER_STATUSES_QUERY_KEY,
    queryObject?.[CUSTOMER_STATUSES_QUERY_KEY] || [],
    ParamTypes.ARRAY
  );

  const [searchBarReloadKey, setSearchBarReloadKey] = useState(0);
  const [isStatsEnabled, setIsStatsEnabled] = useState(!!state?.selectedStat);
  const [isStatBoundaryEnabled, setIsStatBoundaryEnabled] = useState(!!state?.selectedStatBoundary);
  const [noStatBoundaryError, setNoStatBoundaryError] = useState(false);
  const [page, setPage] = useQueryState('page', INITIAL_PAGE, ParamTypes.NUMBER);

  const [isLoadingYearsOrPrograms, setIsLoadingYearsOrPrograms] = useState(false);
  const [isLoadingLocalStorage, setIsLoadingLocalStorage] = useState(false);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [productTestTypesOptions, setProductTestTypesOptions] = useState([]);
  const [customerStatusesOptions, setCustomerStatusesOptions] = useState([]);

  useEffect(() => {
    if (!isStatsEnabled) {
      setIsLoadingLocalStorage(true);
      loadFromLocalStorage()
    }
    setIsLoadingLocalStorage(false);
    clearLocalStorage();
  }, []);


  function updateValueFromLocalStorageKey(key, values) {

    const handlers = {
      [STAT_QUERY_KEY]: (values) => handleChangeSelectedStatistics(values?.[0]),
      [STAT_BOUNDARY_QUERY_KEY]: (values) => setSelectedStatBoundary(values?.[0]),
      [COMPANY_NAME_QUERY_KEY]: (values) => setCompanyName(values?.[0]),
      [PART_LAB_QUERY_KEY]: setPartLab,
      [TEST_NUMBER_QUERY_KEY]: setTestNumber,
      [PRODUCT_NAME_QUERY_KEY]: setProductName,
      [PRODUCT_TEST_TYPES_QUERY_KEY]: (values) => setProductTestTypes(values.map(v => +v)),
      [CUSTOMER_STATUSES_QUERY_KEY]: (values) => setCustomerStatuses(values.map(v => +v))
    };

    if (values) {
      if (handlers[key]) {
        handlers[key](values)
      } else if ([SELECTED_PROGRAM_QUERY_KEY, SELECTED_YEARS_QUERY_KEY].includes(key)) {

        setQueryStringValue(key, values);

        reloadSearchBar();
      }
    }
  }

  const loadFromLocalStorage = () => {
    let storedBlob = localStorage.getItem(SEARCH_STORAGE_KEY);

    if (storedBlob) {
      let queryGroupedParameterEntries = JSON.parse(storedBlob);

      for (const [key, values] of queryGroupedParameterEntries) {
        updateValueFromLocalStorageKey(key, values);
      }
    }
  }

  const saveToLocalStorage = () => {
    let queryParameters = new URLSearchParams(window.location.search);
    let queryParamEntries = Array.from(queryParameters.entries());
    const queryGroupedParameterEntries = Object.entries(
      queryParamEntries.reduce((accumulator, [key, value]) => {
        accumulator[key] = accumulator[key] ? [...accumulator[key], value] : [value];
        return accumulator;
      }, {})
    );

    let storedBlob = JSON.stringify(queryGroupedParameterEntries);
    localStorage.setItem(SEARCH_STORAGE_KEY, storedBlob);
  }

  useEffect(() => {
    setPage(0);
    updateData(rows, isLoadingYearsOrPrograms);
  }, [isLoadingYearsOrPrograms]);

  useEffect(() => {
    let filter = createDetailedStatsFilter(false);
    const searchCompanyFilter = {
      isExport: filter.isExport,
      laboratoryEncryptedId: filter.laboratoryEncryptedId,
      selectedProgram: filter.selectedProgram,
      productTestTypes: filter.productTestTypes,
      order: filter.order,
      orderBy: filter.orderBy,
      rowsPerPage: filter.rowsPerPage,
      page: filter.page
    }
    ProductTestService.getCompanyOptionsForDetailedStatisticsByLaboratory(searchCompanyFilter)
      .then(r => setCompanyOptions(r));

    ProductTestService.getProductTestTypes()
      .then(ptt => setProductTestTypesOptions(ptt));

    ProductTestService.getCustomerStatuses()
      .then(cs => setCustomerStatusesOptions(cs));
  }, []);

  useEffect(() => {
    if(productNumber !== null && productNumber !== 0){
      ProductService.getProductsByNameAndNumber({searchString: productNumber, laboratoryRecid: laboratoryId}).then(resp => {
        setProductName(resp[0].name);
        handleSearchBarChange();
      });
    }
  }, [productNumber]);

  useEffect(() => {
    const productNumberTmp = new URLSearchParams(search).get(PRODUCT_NUMBER_QUERY_KEY);
    if(productNumberTmp !== '0'){
      setProductNumber(productNumberTmp);
    }
  }, [search]);

  useEffect(() => {
    setIsStatsEnabled(!!selectedStat);
  }, [total]);

  const selectProductTest = (ptldId) => {
    saveToLocalStorage()
    history.push(GENERATE_ROUTE(ROUTES.TEST.BASE, { laboratory: laboratoryId, ptldId }));
  };

  const handleSearchBarChange = useCallback(() => {
    setRows([]);
    updateData([], isLoadingYearsOrPrograms);
  }, [selectedStat, selectedStatBoundary]);

  useEffect(() => {
    rows ? setNumberOfRow(rows.length) : setNumberOfRow(0);
  }, [rows]);

  useEffect(() => {
    handleSearchBarChange();
  }, [productTestTypes, customerStatuses, selectedStat, selectedStatBoundary]);

  const handleProductInfochange = (value, setter) => {
    setter(value);
    if(value.length >= 4){
      handleSearchBarChange();
    }
  }

  const handleChangeProductTestTypes = newProductTestTypes => {
    setQueryStringValue('isFromHome', null);

    if (newProductTestTypes === undefined
      || newProductTestTypes === null) {
      setProductTestTypes([]);
    }
    else {
      setProductTestTypes([...newProductTestTypes.map(type => type.ptTypeId)]);
    }
  };

  const handleChangeCustomerStatuses = newCustomerStatuses => {
    setQueryStringValue('isFromHome', null);

    if (newCustomerStatuses === undefined
      || newCustomerStatuses === null) {
      setCustomerStatuses([]);
    }
    else {
      setCustomerStatuses([...newCustomerStatuses.map(status => status.statusId)]);
    }
  };

  function handleChangeSelectedStatistics(value) {
    setSelectedStat(value);
    setIsStatBoundaryEnabled(getHasBoundariesByStat(value));
  }

  const createDetailedStatsFilter = (isForExport) => {
    const queryString = window.location.search.substring(1);
    const filter = {
      laboratoryEncryptedId: laboratoryId,
      selectedStat: selectedStat,
      selectedStatBoundary: selectedStatBoundary,
      ...QueryStringToJSONObject(queryString)
    };
    if (!filter.page) {
      filter.page = INITIAL_PAGE;
    }
    if (!filter.rowsPerPage) {
      filter.rowsPerPage = INITIAL_ROWS_PER_PAGE;
    }
    filter.isExport = isForExport;
    if (isStatsEnabled) {
      const isStatHasBoundary = getHasBoundariesByStat(filter.selectedStat);
      setIsStatBoundaryEnabled(isStatHasBoundary);
      if (!isStatHasBoundary) {
        setSelectedStatBoundary(null);
      }
      filter.partLab = null;
      if (isStatHasBoundary && !filter.selectedStatBoundary) {
        setNoStatBoundaryError(true);
        setIsLoadingProductTestFetch(false);
        setRows([]);
        setTotal(0);
        return;
      }
      setNoStatBoundaryError(false);
    }
    if(productName){
      delete filter.productNumber;
    }
    return filter;
  }

  const updateData = useCallback((oldRows, isLoading) => {
    if (isLoading || (numberOfRow === total && total !== 0)) {
      return;
    }
    setIsLoadingProductTestFetch(true);
    if (previousController.current) {
      previousController.current.abort();
    }
    const controller = new AbortController();
    const { signal } = controller;
    previousController.current = controller;
    const filter = createDetailedStatsFilter(false);
    if (filter) {
      if (companyOptions === []) {
        const searchCompanyFilter = { ...filter, companyName: undefined }
        ProductTestService.getCompanyOptionsForDetailedStatisticsByLaboratory(searchCompanyFilter, signal)
          .then(response => {
            setCompanyOptions(response);
          })
          .catch((errorMessage) => {
            if (errorMessage !== ErrorName.ABORT_ERROR) {
              showDialog({
                variant: DialogType.ERROR,
                title: 'error',
                description: errorMessage
              });
              history.push(GENERATE_ROUTE(ROUTES.ERROR), {errorMessage: errorMessage});
            }
          });
      }
      ProductTestService.getProductTestsDetailedStats(filter, signal)
        .then(response => {
          if (oldRows.length > 0) {
            setRows(addNewRows(oldRows, response.productTestLaboDetails));
          } else {
            setRows(response.productTestLaboDetails);
          }
          if(filter.productNumber){
            setProductName(response.productTestLaboDetails[0]?.productName);
          }
          setTotal(response.total);
          setIsLoadingProductTestFetch(false);
        })
        .catch((errorMessage) => {
          if (errorMessage !== ErrorName.ABORT_ERROR) {
            showDialog({
              variant: DialogType.ERROR,
              title: 'error',
              description: errorMessage
            });
            history.push(GENERATE_ROUTE(ROUTES.ERROR), { errorMessage: errorMessage });
          }
        });
    }
  }, [selectedStat, selectedStatBoundary, rows, isLoadingYearsOrPrograms]);

  const handleChangePage = (newPage) => {
    setPage(newPage);
    updateData(rows, isLoadingYearsOrPrograms);
  };

  const onScroll = () => {
    if (listInnerRef.current) {
      const {
        scrollTop,
        scrollHeight,
        clientHeight
      } = listInnerRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 1 && !isLoadingProductTestFetch && rows.length < total) {
        const newPage = page ? page + 1 : 1;
        handleChangePage(newPage);
      }
    }
  };

  const handleExport = () => {
    setIsLoadingExport(true);
    if ((total ?? rows.length) > 0) {
      const filter = createDetailedStatsFilter(true);
      ProductTestService.getProductTestsDetailedStats(filter)
        .then(resp => {
          const date = new Date();
          const time = date.getTime();
          const ptlds = [];
          resp.productTestLaboDetails.forEach((ptld) => {
            ptlds.push(mapProductRecords(ptld));
          });
          convertJsonToXlsx(ptlds, `${localize('detailedStatisticsExport')}${date.toLocaleDateString()}-${time}`);
          setIsLoadingExport(false);
        })
        .catch((errorMessage) => {
          setIsLoadingExport(false);
          if (errorMessage !== ErrorName.ABORT_ERROR) {
            showDialog({
              variant: DialogType.ERROR,
              title: 'error',
              description: errorMessage
            });
            history.push(GENERATE_ROUTE(ROUTES.ERROR), { errorMessage: errorMessage });
          }
        });
    } else {
      setIsLoadingExport(false);
      showDialog({
        variant: DialogType.ERROR,
        title: 'error',
        description: localize('noDataToExport')
      });
    }
  };

  function clearSearchParams() {
    let url = new URL(window.location);
    url.search = "";
    window.history.pushState('', '', url);
  }

  function reloadSearchBar() {
    setSearchBarReloadKey(key => key + 1);
  }

  function clearLocalStorage() {
    localStorage.removeItem(SEARCH_STORAGE_KEY);
  }

  function clearFieldStates() {
    setCompanyName(null);
    setPartLab(null);
    setSelectedStat(null);
    setSelectedStatBoundary(null);
    setTestNumber("");
    setProductName("");
    setProductTestTypes([]);
    setCustomerStatuses([]);
  }

  function resetForm() {
    clearLocalStorage();
    clearFieldStates();
    clearSearchParams();
    reloadSearchBar();
  }

  return (
    <PageContainer>
      <Grid container direction='column'>
        <Grid item>
          <SearchBar
            displayRadioYearsFilter={false}
            handleOnSearch={handleSearchBarChange}
            isPreloadingData={isLoadingLocalStorage}
            key={searchBarReloadKey}
            setIsLoadingYearsOrPrograms={setIsLoadingYearsOrPrograms}
          />
        </Grid>
        { !productNumber &&
          <Grid item>
            <Grid
              alignItems="center"
              container
              direction="row"
              justifyContent="space-evenly"
            >
              <AutoCompleteWithValue
                label='company'
                onChange={value => { setCompanyName(value); handleSearchBarChange(value); }}
                options={companyOptions}
                value={companyName}
              />
              { !isStatsEnabled &&
                <Grid item>
                  <Grid>
                    <Grid item>
                      <AutoCompleteWithValue
                        isTranslatingOptions
                        label='partLab'
                        onChange={value => { setPartLab(value); handleSearchBarChange(value); }}
                        options={PART_LAB}
                        value={partLab}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              }
              { isStatsEnabled &&
                <AutoCompleteWithValue
                  isTranslatingOptions
                  label="statistic"
                  onChange={value => {
                    handleChangeSelectedStatistics(value);
                  }}
                  options={getStats()}
                  value={selectedStat}
                />
              }
              { isStatBoundaryEnabled &&
                <AutoCompleteWithValue
                  error={noStatBoundaryError}
                  helperText={localize('noStatBoundaryError')}
                  isTranslatingOptions
                  label="statisticBoundary"
                  onChange={value => {setSelectedStatBoundary(value); handleSearchBarChange();}}
                  options={COLUMN_CHART_AXIS_LABEL}
                  value={selectedStatBoundary}
                />
              }
            </Grid>
          </Grid>
        }
        <Grid item>
          <Grid
            alignItems="center"
            container
            direction="row"
            justifyContent="flex-start"
            spacing={2}
          >
            <Grid item xs={3}>
              <TextField
                label={localize("productTestNumber")}
                onChange={e => {handleProductInfochange(e.target.value, setTestNumber);handleSearchBarChange();}}
                style={{width: '90%', marginLeft: '25px'}}
                value={testNumber}
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={localize("productName")}
                onChange={e => {handleProductInfochange(e.target.value, setProductName);handleSearchBarChange();}}
                style={{width: '90%'}}
                value={productName}
              />
            </Grid>
            <Grid item xs={3}>
              <AutoCompleteWithValue
                getOptionLabel={option => option.ptTypeTranslated || option}
                getOptionSelected={(option, value) => option.ptTypeId === value.ptTypeId}
                isMultiple
                label='productTestTypes'
                onChange={value => handleChangeProductTestTypes(value)}
                options={productTestTypesOptions}
                value={productTestTypesOptions?.filter(option => productTestTypes.includes(option.ptTypeId)) || []}
              />
            </Grid>
            <Grid item xs={3}>
              <AutoCompleteWithValue
                getOptionLabel={option => option.statusTranslated || option}
                getOptionSelected={(option, value) => option.statusId === value.statusId}
                isMultiple
                label='customerStatuses'
                onChange={value => handleChangeCustomerStatuses(value)}
                options={customerStatusesOptions}
                value={customerStatusesOptions?.filter(opt => customerStatuses.includes(opt.statusId)) || []}
              />
            </Grid>
            <Grid item xs={3}>
              <CustomButton
                onClick={() => resetForm()}>
                {localize('reset')}
              </CustomButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <br/>
      <Grid className={classes.resultTitle} item>
        {`${localize('results')} ${numberOfRow}/${total}`}
      </Grid>
      <Grid className={classes.tableContainer} innerRef={listInnerRef} item onScroll={onScroll}>
        <CustomTableWithExternalPagination
          headers={testRows}
          initialOrderBy="companyName"
          isBackend
          isLoading={isLoadingProductTestFetch}
          onChange={handleSearchBarChange}
          onRowCLick={(row) => selectProductTest(row.id)}
          page={page}
          rows={rows}
          rowsPerPage={INITIAL_ROWS_PER_PAGE}
          setPage={setPage}
          stickyHeader
        />
      </Grid>
      <Grid alignItems="center" className={classes.tableFooter} container justifyContent="space-between">
        <Grid item>
          <ExcelExportButton
            handleExport={handleExport}
            isLoadingExport={isLoadingExport}
          />
        </Grid>
      </Grid>
    </PageContainer>
  );
};